import React, { useEffect, useState } from "react";

/**
 * Minimal confirm/cancel modal — nothing like it existed anywhere in this
 * codebase before item 10 (no modal primitive, no window.confirm() usage).
 * Needed in 3+ places within this one item (promote/demote confirmation,
 * remove teammate, leave team), which clears this project's bar for a
 * shared primitive rather than one-off inline UI.
 *
 * Item 24 added three optional props for account deletion. All are opt-in
 * and every pre-existing caller (which passes a plain string `message` and
 * none of the new props) behaves exactly as before:
 *
 *   message            — widened from string to ReactNode. Deletion needs
 *                        several paragraphs, and nesting block elements in
 *                        the old <p> would have been invalid HTML.
 *   confirmationPhrase — when set, the user must type it exactly before
 *                        Confirm enables. Reserved for the genuinely
 *                        irreversible case (deleting a team's entire
 *                        history), matching what GitHub and Stripe gate the
 *                        same way.
 *   busy / busyLabel   — request in flight: keeps the dialog open, disables
 *                        both buttons, swaps the confirm label, and
 *                        suppresses Escape/backdrop dismissal. Without it
 *                        the caller's usual "close then await" pattern
 *                        would drop the dialog and leave the user staring
 *                        at a page that is about to be destroyed — and a
 *                        double-click would fire two requests.
 */
function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  danger = false,
  confirmationPhrase = null,
  busy = false,
  busyLabel = "Working…",
  onConfirm,
  onCancel,
}) {
  const [typed, setTyped] = useState("");

  // Reset on each open so a previous attempt's text can't pre-satisfy the
  // gate the next time the dialog is shown.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  useEffect(() => {
    if (!open || busy) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const phraseSatisfied = !confirmationPhrase || typed.trim() === confirmationPhrase;
  const confirmDisabled = busy || !phraseSatisfied;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="w-full max-w-sm bg-rf-bg-secondary border border-rf-border rounded-xl p-6 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-rf-text-primary mb-2">{title}</h3>
        <div className="text-sm text-rf-text-secondary mb-6">{message}</div>

        {confirmationPhrase && (
          <div className="mb-6">
            <label htmlFor="confirm-phrase" className="block text-xs text-rf-text-secondary mb-1.5">
              Type <span className="font-semibold text-rf-text-primary">{confirmationPhrase}</span> to confirm
            </label>
            <input
              id="confirm-phrase"
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
              autoComplete="off"
              className="w-full px-3.5 py-2.5 rounded-lg border border-rf-border bg-rf-bg-tertiary text-rf-text-primary text-sm outline-none focus:border-rf-accent-danger/50 disabled:opacity-50"
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-md border border-rf-border text-rf-text-secondary text-sm font-medium hover:text-rf-text-primary transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none ${
              danger
                ? "bg-rf-accent-danger/15 text-rf-accent-danger border border-rf-accent-danger/30 hover:bg-rf-accent-danger/25"
                : "bg-rf-accent/15 text-rf-accent border border-rf-accent/30 hover:bg-rf-accent/25"
            }`}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
